import {
  ConnectionState,
  Room,
  RoomEvent,
  RemoteTrack,
  Track,
} from 'livekit-client';

import createAvSyncLogger from './avatar-av-sync-logger';

export type AvatarConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

// How often the sync controller reads jitter buffers and adjusts video delay.
const SYNC_INTERVAL_MS = 2000;

// Ignore jitter buffer differences below this — small enough to be
// imperceptible and avoids chasing normal jitter-buffer fluctuation.
const SYNC_DEADBAND_MS = 30;

// Steady-state video delay cap. Covers the normal LiveAvatar pipeline
// offset without visible video lag.
const MAX_VIDEO_DELAY_SEC = 0.3;

// Higher cap used during pipeline warm-up. LiveAvatar's internal audio
// pipeline introduces ~1-2s of jitter buffer inflation on the first
// utterance regardless of server-side pacing. The initial cap lets the
// sync controller compensate more aggressively; once the buffer drains
// (syncError stays below the steady-state cap for SETTLE_READINGS in a
// row), the cap tightens permanently for the session.
const MAX_VIDEO_DELAY_INITIAL_SEC = 1.2;
const SETTLE_READINGS = 2;

// Raw cumulative counters from a single inbound-rtp stat.
type RawJitterCounters = {
  delay: number;
  emitted: number;
  packetsReceived: number;
};

// The current (delta-based) jitter buffer delay.
type CurrentJitter = {
  currentMs: number;
  raw: RawJitterCounters;
};

const readRawCounters = (report: RTCStatsReport | undefined): RawJitterCounters | null => {
  if (!report) return null;
  let result: RawJitterCounters | null = null;
  report.forEach((stat: any) => {
    if (stat.type !== 'inbound-rtp') return;
    if (typeof stat.jitterBufferDelay !== 'number' || typeof stat.jitterBufferEmittedCount !== 'number') return;
    result = {
      delay: stat.jitterBufferDelay,
      emitted: stat.jitterBufferEmittedCount,
      packetsReceived: typeof stat.packetsReceived === 'number' ? stat.packetsReceived : 0,
    };
  });
  return result;
};

const computeDeltaJitter = (
  now: RawJitterCounters,
  prev: RawJitterCounters | null,
): CurrentJitter | null => {
  if (!prev) return null;
  const dEmitted = now.emitted - prev.emitted;
  if (dEmitted <= 0) return null;
  const dDelay = now.delay - prev.delay;
  return { currentMs: Math.round((dDelay / dEmitted) * 1000), raw: now };
};

export type AvatarVideoPipeline = {
  // Connects to the LiveKit room. Safe to call again with fresh credentials
  // (e.g. after the avatar session is re-minted) — any previous connection is
  // torn down first.
  connect: (livekitUrl: string, token: string) => Promise<void>;
  // Attaches the avatar's video+audio tracks to the given element. Tracks that
  // were subscribed before attach is called are attached retroactively, and
  // tracks arriving later attach as they come in. Pass null to detach.
  attach: (videoEl: HTMLVideoElement | null) => void;
  // Builds a MediaStream of the avatar's currently subscribed video+audio
  // tracks for recording. Returns null until at least one track has arrived.
  // Grab a fresh stream for every recording — after a re-mint/rejoin the
  // remote tracks are replaced and a previously returned stream goes stale.
  getRecordingStream: () => MediaStream | null;
  onConnectionState: (cb: (state: AvatarConnectionState) => void) => void;
  // Holds the avatar's video back by the given number of seconds so it can be
  // aligned with audio that plays late. The video jitter buffer honours this
  // almost immediately, unlike NetEQ on the audio side, which is why correcting
  // on the video track beats waiting for libwebrtc to ramp the audio.
  setVideoPlayoutDelay: (seconds: number) => void;
  // Debug: logs a correlation marker (e.g. avatar_speech_started) and starts a
  // dense A/V stats sampling burst around it. Passive — never affects playback.
  markAvSyncEvent: (label: string, data?: Record<string, unknown>) => void;
  cleanup: () => void;
};

const toAvatarConnectionState = (state: ConnectionState): AvatarConnectionState => {
  switch (state) {
    case ConnectionState.Connecting:
      return 'connecting';
    case ConnectionState.Connected:
      return 'connected';
    case ConnectionState.Reconnecting:
    case ConnectionState.SignalReconnecting:
      return 'reconnecting';
    default:
      return 'disconnected';
  }
};

// Watches the LiveAvatar's LiveKit room and renders its video+audio into a
// <video> element. The avatar's audio track carries the AI's speech in video
// mode — the browser never plays audio_delta chunks itself.
const createAvatarVideoPipeline = (): AvatarVideoPipeline => {
  const room = new Room();
  const avSyncLogger = createAvSyncLogger();
  let videoEl: HTMLVideoElement | null = null;
  // Tracks seen so far, so attach() can bind ones that arrived before the element.
  const subscribedTracks = new Set<RemoteTrack>();
  let connectionStateCallback: ((state: AvatarConnectionState) => void) | null = null;
  let isCleanedUp = false;
  // True while connect() tears down a previous connection on purpose — that
  // disconnect must not surface as a room drop (the caller re-mints the avatar
  // session on drops, which would loop forever).
  let isDisconnectingIntentionally = false;
  let syncProbeTimer: ReturnType<typeof setInterval> | null = null;

  const attachTrack = (track: RemoteTrack) => {
    if (!videoEl) return;
    // Both the video and audio tracks render through the same element — LiveKit
    // wires audio tracks to the element's audio output when attached this way.
    track.attach(videoEl);
  };

  const detachAllTracks = () => {
    subscribedTracks.forEach((track) => track.detach());
  };

  const findTrack = (kind: Track.Kind): RemoteTrack | null => Array.from(subscribedTracks).find((track) => track.kind === kind) ?? null;

  // Current video playout delay we've applied, in seconds.
  let currentVideoDelay = 0;
  // Previous raw counters for delta computation — null until first sample.
  let prevAudio: RawJitterCounters | null = null;
  let prevVideo: RawJitterCounters | null = null;
  // Adaptive cap: starts high, tightens once the buffer settles.
  let effectiveCap = MAX_VIDEO_DELAY_INITIAL_SEC;
  let belowSteadyCapCount = 0;

  // Delta-based A/V sync controller. Uses the jitter buffer delay of
  // samples emitted since the LAST reading (not the cumulative lifetime
  // average) so the signal tracks the actual current buffer depth. The
  // cumulative average is dominated by the opening burst for minutes —
  // the delta converges in one interval.
  const syncStep = async () => {
    const audioTrack = findTrack(Track.Kind.Audio);
    const videoTrack = findTrack(Track.Kind.Video);
    if (!audioTrack || !videoTrack) return;

    const [audioRaw, videoRaw] = await Promise.all([
      audioTrack.getRTCStatsReport().then(readRawCounters),
      videoTrack.getRTCStatsReport().then(readRawCounters),
    ]);

    if (!audioRaw || !videoRaw) return;

    const audioDelta = computeDeltaJitter(audioRaw, prevAudio);
    const videoDelta = computeDeltaJitter(videoRaw, prevVideo);
    prevAudio = audioRaw;
    prevVideo = videoRaw;

    // First sample — just storing counters, can't compute delta yet.
    if (!audioDelta || !videoDelta) return;

    // Positive = audio's current buffer deeper than video's = audio plays
    // late = lips lead voice. Clamp to [0, cap] — overshoot freezes video.
    const syncErrorMs = audioDelta.currentMs - videoDelta.currentMs;
    const rawTarget = syncErrorMs > SYNC_DEADBAND_MS ? syncErrorMs / 1000 : 0;

    // Tighten the cap once the buffer has settled below steady-state.
    if (rawTarget <= MAX_VIDEO_DELAY_SEC) {
      belowSteadyCapCount += 1;
      if (belowSteadyCapCount >= SETTLE_READINGS) {
        effectiveCap = MAX_VIDEO_DELAY_SEC;
      }
    } else {
      belowSteadyCapCount = 0;
    }

    const targetDelaySec = Math.min(rawTarget, effectiveCap);

    if (targetDelaySec !== currentVideoDelay) {
      videoTrack.setPlayoutDelay(targetDelaySec);
      currentVideoDelay = targetDelaySec;
    }

    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] avSync', {
      syncErrorMs,
      videoDelaySec: currentVideoDelay,
      cap: effectiveCap,
      audioCurrentMs: audioDelta.currentMs,
      videoCurrentMs: videoDelta.currentMs,
      audioPacketsReceived: audioRaw.packetsReceived,
      videoPacketsReceived: videoRaw.packetsReceived,
    });
  };

  const stopSyncController = () => {
    if (syncProbeTimer === null) return;
    clearInterval(syncProbeTimer);
    syncProbeTimer = null;
  };

  const startSyncController = () => {
    stopSyncController();
    syncProbeTimer = setInterval(() => {
      syncStep().catch(() => {});
    }, SYNC_INTERVAL_MS);
  };

  room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, publication: any, participant: any) => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] TrackSubscribed', {
      kind: track.kind,
      sid: track.sid,
      source: publication?.source,
      participant: participant?.identity,
      muted: track.isMuted,
      subscribedTracks: subscribedTracks.size + 1,
    });
    subscribedTracks.add(track);
    avSyncLogger.onTrackSubscribed(track);
    attachTrack(track);
  });

  room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] TrackUnsubscribed', {
      kind: track.kind,
      sid: track.sid,
      subscribedTracks: subscribedTracks.size - 1,
    });
    subscribedTracks.delete(track);
    avSyncLogger.onTrackUnsubscribed(track);
    track.detach();
  });

  room.on(RoomEvent.TrackMuted, (publication: any, participant: any) => {
    // eslint-disable-next-line no-console
    console.warn('[AvatarPipeline] TrackMuted', {
      kind: publication?.kind,
      source: publication?.source,
      participant: participant?.identity,
    });
  });

  room.on(RoomEvent.TrackUnmuted, (publication: any, participant: any) => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] TrackUnmuted', {
      kind: publication?.kind,
      source: publication?.source,
      participant: participant?.identity,
    });
  });

  room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] ConnectionStateChanged', state);
    if (isCleanedUp) return;
    if (state === ConnectionState.Connected) startSyncController();
    else stopSyncController();
    if (isDisconnectingIntentionally && state === ConnectionState.Disconnected) return;
    connectionStateCallback?.(toAvatarConnectionState(state));
  });

  room.on(RoomEvent.Reconnecting, () => {
    // eslint-disable-next-line no-console
    console.warn('[AvatarPipeline] Reconnecting to LiveKit room');
  });

  room.on(RoomEvent.Reconnected, () => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] Reconnected to LiveKit room');
  });

  room.on(RoomEvent.SignalConnected, () => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] SignalConnected');
  });

  room.on(RoomEvent.MediaDevicesError, (error: Error) => {
    // eslint-disable-next-line no-console
    console.error('[AvatarPipeline] MediaDevicesError', error);
  });

  const connect = async (livekitUrl: string, token: string) => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] connect()', { livekitUrl, currentState: room.state });
    // Reconnect path (re-minted avatar session) — drop the previous room state
    // before joining with the fresh credentials.
    if (room.state !== ConnectionState.Disconnected) {
      isDisconnectingIntentionally = true;
      try {
        detachAllTracks();
        subscribedTracks.clear();
        await room.disconnect();
      } finally {
        isDisconnectingIntentionally = false;
      }
    }
    await room.connect(livekitUrl, token);
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] connected', { state: room.state, participants: room.remoteParticipants.size });
    avSyncLogger.markEvent('room_connected', { participants: room.remoteParticipants.size });
  };

  const getRecordingStream = (): MediaStream | null => {
    const mediaStreamTracks = Array.from(subscribedTracks)
      .map((track) => track.mediaStreamTrack)
      .filter((mediaStreamTrack): mediaStreamTrack is MediaStreamTrack => Boolean(mediaStreamTrack));
    if (!mediaStreamTracks.length) return null;
    return new MediaStream(mediaStreamTracks);
  };

  const attach = (el: HTMLVideoElement | null) => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] attach()', { hasElement: Boolean(el), subscribedTracks: subscribedTracks.size });
    if (videoEl && videoEl !== el) {
      detachAllTracks();
    }
    videoEl = el;
    avSyncLogger.attachMediaElement(el);
    if (videoEl) {
      // The pivotal warm-up event: WebRTC only decodes/presents media once a
      // track has a sink, so playout effectively begins here — not at
      // TrackSubscribed. Marked so the burst sampler brackets it.
      avSyncLogger.markEvent('media_element_attached', { subscribedTracks: subscribedTracks.size });
      subscribedTracks.forEach(attachTrack);
    }
  };

  const onConnectionState = (cb: (state: AvatarConnectionState) => void) => {
    connectionStateCallback = cb;
  };

  const setVideoPlayoutDelay = (seconds: number) => {
    const videoTrack = findTrack(Track.Kind.Video);
    if (!videoTrack) return;
    videoTrack.setPlayoutDelay(seconds);
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] setVideoPlayoutDelay', { seconds });
  };

  const cleanup = () => {
    // eslint-disable-next-line no-console
    console.info('[AvatarPipeline] cleanup()', { subscribedTracks: subscribedTracks.size, roomState: room.state });
    isCleanedUp = true;
    avSyncLogger.cleanup();
    stopSyncController();
    connectionStateCallback = null;
    detachAllTracks();
    subscribedTracks.clear();
    videoEl = null;
    room.removeAllListeners();
    room.disconnect().catch(() => {});
  };

  return {
    connect,
    attach,
    getRecordingStream,
    onConnectionState,
    setVideoPlayoutDelay,
    markAvSyncEvent: avSyncLogger.markEvent,
    cleanup,
  };
};

export default createAvatarVideoPipeline;
